<script setup lang="ts">
import BaseButton from "@/components/Base/Button.vue";
import BaseModal from "@/components/Base/Modal.vue";
import { type BootstrapIconName } from "@/types/bootstrap-icons";

defineOptions({ name: "BaseConfirmModal", inheritAttrs: false });

const { t } = useI18n();

type Props = {
  open?: boolean;
  title: string;
  confirmText?: string;
  cancelText?: string;
  confirmKind?: "primary" | "secondary" | "danger";
  confirmIcon?: BootstrapIconName;
  loading?: boolean;
  confirmDisabled?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "fullscreen";
  showCancelButton?: boolean;
};

withDefaults(defineProps<Props>(), {
  confirmKind: "danger",
  loading: false,
  confirmDisabled: false,
  size: "md",
  showCancelButton: true,
});

const emit = defineEmits<{
  confirm: [];
  cancel: [];
}>();

const handleConfirm = (): void => {
  emit("confirm");
};

const handleCancel = (): void => {
  emit("cancel");
};
</script>

<template>
  <BaseModal
    v-bind="$attrs"
    :open="open"
    :title="title"
    :size="size"
    :backdrop="loading ? 'static' : true"
    :keyboard="!loading"
    @hide="handleCancel"
  >
    <slot />

    <template #footer>
      <BaseButton
        v-if="showCancelButton"
        kind="tertiary"
        type="button"
        :disabled="loading"
        data-bs-dismiss="modal"
      >
        {{ cancelText ?? t("buttons.cancel") }}
      </BaseButton>

      <BaseButton
        :kind="confirmKind"
        type="button"
        :disabled="loading || confirmDisabled"
        :icon-name="confirmIcon"
        icon-position="right"
        @click="handleConfirm"
      >
        {{ confirmText ?? t("buttons.delete") }}
      </BaseButton>
    </template>
  </BaseModal>
</template>
